// Access → Connected agents: the MCP connections the signed-in person made
// from Claude Code, claude.ai or ChatGPT to this environment's aura-mcp, with
// the scopes they ticked, and Revoke. Each connection is one OAuth grant
// (app/api/studio_mcp_router.py); revoking it ends all of its tokens.

import { useEffect, useState } from "react";
import { formatDateTime } from "../components/analytics/format";
import { clientLabel, holdsRelease, scopeChips } from "../components/connectedAgents/format";
import { listConnectedAgents, revokeConnectedAgent, type ConnectedAgent } from "../services/mcp";

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function Scopes({ scopes }: { scopes: string[] }) {
  return (
    <span className="chip-row">
      {scopeChips(scopes).map((c) => (
        <span
          key={c.scope}
          className={`chip mono${c.level === "release" ? " chip-flag" : ""}`}
          title={c.level === "release" ? "Can publish, promote, roll back and change rollouts" : undefined}
        >
          {c.scope}
        </span>
      ))}
    </span>
  );
}

export function ConnectedAgentsPage() {
  const [agents, setAgents] = useState<ConnectedAgent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Bumped after a revoke to reload the list.
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    let cancelled = false;
    listConnectedAgents()
      .then((found) => {
        if (cancelled) return;
        setAgents(found);
        setError(null);
      })
      .catch((e) => !cancelled && setError(errorMessage(e)));
    return () => {
      cancelled = true;
    };
  }, [reloads]);

  async function revoke(agent: ConnectedAgent) {
    const name = clientLabel(agent.clientId, agent.clientName);
    if (!confirm(`Revoke ${name}? It stops working within a minute, and connecting it again needs a new sign-in.`)) {
      return;
    }
    setBusy(agent.grantId);
    setNotice(null);
    try {
      await revokeConnectedAgent(agent.grantId);
      setNotice(`${name} revoked.`);
      setReloads((n) => n + 1);
    } catch (e) {
      // A notice, not `error`: the list stays up, and the person can try again.
      setNotice(`Revoking ${name} failed: ${errorMessage(e)}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="list-page">
      <div className="list-toolbar">
        <h1>Connected agents</h1>
      </div>
      <p className="note">
        The AI agents you connected to this environment's Aura MCP server. Each one acts as you, with the scopes
        you ticked when you signed in. Revoke one to end it: it can't renew its token, and the token it holds
        stops working within a minute. Release actions it ran show an MCP badge in a talk track's History.
      </p>
      {notice && <div className="note">{notice}</div>}

      {error ? (
        <div className="state-box error">Connected agents unavailable ({error}).</div>
      ) : !agents ? (
        <div className="state-box">Loading connected agents…</div>
      ) : agents.length === 0 ? (
        <div className="state-box">No agents are connected as you to this environment.</div>
      ) : (
        <div className="table-scroll">
          <table className="guide-table">
            <thead>
              <tr>
                <th className="no-sort">agent</th>
                <th className="no-sort">scopes</th>
                <th className="no-sort">connected</th>
                <th className="no-sort">last used</th>
                <th className="no-sort">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr key={a.grantId}>
                  <td>
                    <strong>{clientLabel(a.clientId, a.clientName)}</strong>
                    {holdsRelease(a.scopes) && <span className="badge badge-disabled tt-small">release</span>}
                    <div className="muted tt-small mono">{a.clientId}</div>
                  </td>
                  <td>
                    <Scopes scopes={a.scopes} />
                  </td>
                  <td className="voice-cell">{formatDateTime(a.createdAt)}</td>
                  <td className="voice-cell">{a.lastUsedAt ? formatDateTime(a.lastUsedAt) : "never"}</td>
                  <td>
                    <div className="row-actions">
                      <button
                        className="btn btn-sm btn-danger"
                        disabled={busy !== null}
                        onClick={() => revoke(a)}
                      >
                        {busy === a.grantId ? "Revoking…" : "Revoke"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
